// claimResolver — Capstone GATE A (Phase 6, supabase-mongo-auth-capstone.md).
//
// Mongo-sourced claim READS for the intranet permission / role / superadmin
// gates. The frontend already prefers the Mongo SDK token over the retired
// governance Supabase JWT for transport (`shared/functions/sdk.js`
// `_workspaceTokenProvider`); this module resolves the same primitives the
// gates read — `root.jwtClaims` is now itself SDK/Mongo-sourced (see
// `shared/functions/authBridge.js`) — by re-deriving the permission-sensitive
// fields from `/auth/me`'s per-org enrichment, which is more current than
// whatever snapshot is sitting on `root.jwtClaims`:
//
//   • `permissions[]`  — the intranet permission union (OrgMember role ∪ team perms)
//   • `is_owner`       — active-org ownership (`simpleOrgRole(role) === 'owner'`)
//   • `org_role`       — active-org simple role (owner | admin | member)
//   • `symbols_role`   — platform global role (`User.globalRole`)
//
// This module centralizes those reads in ONE place (`resolveClaims`) so every
// call site stays consistent. Was flag-gated behind `__USE_MONGO_CLAIMS__`
// during the Gate A cutover (2026-07-03); collapsed to Mongo-only 2026-07-31
// after the governance JWT read-dependency was confirmed permanently dead —
// the server stopped minting `app_metadata` claims on 2026-07-28, so the old
// OFF branch (`root.jwtClaims` verbatim) had no live data source left.

import {
  PERMISSION_KEYS,
  ORG_ROLE_PERMISSIONS,
  CATEGORY_PERMISSION_TEMPLATES
} from './permissions.js'

// 6-tier OrgMember.role → 3-tier intranet axis. Mirrors the server's
// `simpleOrgRole` (core/constants/permissions.js) — keep in sync. Used to
// derive `org_role` / `is_owner` from the raw active-org role when the
// server hasn't pre-enriched it.
const _SIMPLE_ROLE_MAP = {
  owner: 'owner',
  'co-owner': 'owner',
  admin: 'admin',
  maintainer: 'admin',
  member: 'member',
  contributor: 'member'
}
const _simpleOrgRole = (role) => (role ? _SIMPLE_ROLE_MAP[role] || 'member' : 'member')

// Local permission union — fallback for when the server hasn't enriched
// `org.permissions` (older /auth/me). Mirrors the server's
// `unionEffectivePermissions` (core/utils/effectivePermissions.js): owner →
// every key; otherwise OrgMember-role perms ∪ each team's perms, with passive
// members intersected to the passive template (today empty → zero elevations).
const _unionPermissions = (memberRole, teamsInOrg) => {
  const simple = _simpleOrgRole(memberRole)
  if (simple === 'owner') return PERMISSION_KEYS.slice()
  const merged = new Set(ORG_ROLE_PERMISSIONS[simple] || [])
  const keySet = new Set(PERMISSION_KEYS)
  const passiveSet = new Set(CATEGORY_PERMISSION_TEMPLATES.passive || [])
  for (const t of Array.isArray(teamsInOrg) ? teamsInOrg : []) {
    const isPassive = t && t.status === 'passive'
    const perms = Array.isArray(t && t.permissions) ? t.permissions : []
    for (const p of perms) {
      if (!keySet.has(p)) continue
      if (isPassive && !passiveSet.has(p)) continue
      merged.add(p)
    }
  }
  return [...merged]
}

// Resolve the active-org row from the Mongo-sourced state. `root.org` is the
// active-org OrgMember row (`{ id, role, … }`) populated by
// subscribeAuthSession / the eager scope fetch from `/auth/me`. Falls back to
// looking up `workspace_org_id` in `root.orgs` when `root.org` is unset.
const _activeOrg = (root) => {
  if (!root) return null
  if (root.org) return root.org
  const activeId = root.jwtClaims?.workspace_org_id
  const orgs = Array.isArray(root.orgs) ? root.orgs : []
  return (activeId && orgs.find((o) => o && o.id === activeId)) || orgs[0] || null
}

// Teams the user belongs to in the active org — for the local permission
// union fallback. The server-enriched `org.permissions` is preferred; this
// only runs when that's absent. Team lists may arrive on `root.jwtClaims.teams`
// (mirrored session claims) or `root.teams` (HR roster) — both are tolerated.
const _activeOrgTeams = (root, activeOrgId) => {
  const claimsTeams = Array.isArray(root?.jwtClaims?.teams) ? root.jwtClaims.teams : null
  if (claimsTeams) {
    return activeOrgId
      ? claimsTeams.filter((t) => !t.org_id || String(t.org_id) === String(activeOrgId))
      : claimsTeams
  }
  return []
}

// Build the Mongo-sourced claim object — field-for-field equivalent to the
// shape the gates read, sourced from `/auth/me`. PRESERVES every
// non-permission field already on `root.jwtClaims` (orgs, workspaces,
// active_workspace_id, workspace_org_id, teams, sub, …) so consumers that read
// those keys through the same resolver are unaffected — ONLY the permission /
// role primitives are re-sourced from Mongo.
const _buildMongoClaims = (root) => {
  const base = (root && root.jwtClaims) || {}
  const org = _activeOrg(root)
  const rawRole = org?.role || null
  // Prefer server-enriched fields (single source of truth — the server's
  // sessionClaims union); derive locally only when absent.
  const orgRole =
    org && typeof org.simpleRole === 'string' ? org.simpleRole : _simpleOrgRole(rawRole)
  const isOwner = org && typeof org.isOwner === 'boolean' ? org.isOwner : orgRole === 'owner'
  let permissions
  if (org && Array.isArray(org.permissions)) {
    permissions = org.permissions
  } else if (org && Array.isArray(org.intranetPermissions)) {
    permissions = org.intranetPermissions
  } else {
    permissions = _unionPermissions(rawRole, _activeOrgTeams(root, org?.id))
  }
  // `symbols_role` ← Mongo `User.globalRole` (root.user.globalRole, surfaced by
  // /auth/me). Falls back to the existing claim when the user record isn't on
  // root yet.
  const globalRole =
    (root && root.user && root.user.globalRole) || base.symbols_role || base.symbolsRole || null
  return {
    ...base,
    permissions,
    is_owner: isOwner,
    org_role: orgRole,
    symbols_role: globalRole,
    symbolsRole: globalRole
  }
}

// THE single choke point. Every claim-read site calls this so they resolve
// consistently. Null-safe: returns `{}` when there's no claim source
// (matching the historical `|| {}` posture).
export const shellResolveClaims = (root) => {
  if (!root) return {}
  return _buildMongoClaims(root)
}
